import { useState, useEffect } from 'react'
import { useFrontDoorCalc } from '../../hooks/useCalculate'
import { useAnalytics } from '../../hooks/useAnalytics'
import { CurrencyTooltip } from '../shared/CurrencyTooltip'
import { FormulaDisclosure } from '../shared/FormulaDisclosure'
import { DiscountModifier } from '../shared/DiscountModifier'
import { MobileCostBar } from '../shared/MobileCostBar'
import { FeedbackModal } from '../shared/FeedbackModal'
import { Disclaimer } from '../shared/Disclaimer'
import { ResourcesSection } from '../shared/ResourcesSection'
import { ShareButton } from '../shared/ShareButton'
import { Slider } from '../ui/Slider'
import { Toggle } from '../ui/Toggle'
import { Select } from '../ui/Select'
import { FieldTooltip } from '../shared/FieldTooltip'
import type { FrontDoorRequest } from '../../api/types'
import { useCalculatorStore } from '../../store/calculatorStore'

const defaultInputs: FrontDoorRequest = {
  tier: 'standard',
  requestsPerMonth: 10_000_000,
  dataTransfer: {
    inboundGB: 0,
    outboundGB: 500,
  },
  waf: {
    enabled: false,
    customRuleCount: 0,
  },
  ddos: {
    tier: 'none',
    protectedIPCount: 1,
  },
}

const TIER_OPTIONS = [
  { value: 'standard', label: 'Standard', description: 'Modern baseline tier' },
  { value: 'premium', label: 'Premium', description: 'Higher security feature set' },
] as const

const DDOS_OPTIONS = [
  { value: 'none', label: 'None', description: 'No additional DDoS plan' },
  { value: 'network_protection', label: 'Network Protection', description: 'Flat plan, up to 100 IPs' },
] as const

export default function FrontDoorCalc() {
  const [inputs, setInputs] = useState<FrontDoorRequest>(defaultInputs)
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const { discount, setDiscount } = useCalculatorStore()
  const { data, isLoading, isError } = useFrontDoorCalc(inputs)
  const { trackCalculatorUsed } = useAnalytics()
  useEffect(() => {
    if (data?.monthly_estimate) {
      trackCalculatorUsed({ calculator: 'front-door', result_gbp: data.monthly_estimate, tier: inputs.tier })
    }
  }, [data?.monthly_estimate])

  const visibleTotal = data ? data.monthly_estimate * (1 - discount / 100) : null

  function set<K extends keyof FrontDoorRequest>(section: K, value: FrontDoorRequest[K]) {
    setInputs((prev) => ({ ...prev, [section]: value }))
  }

  function patchNested<K extends 'dataTransfer' | 'waf' | 'ddos'>(section: K, patch: Partial<FrontDoorRequest[K]>) {
    setInputs((prev) => ({ ...prev, [section]: { ...prev[section], ...patch } }))
  }

  return (
    <div className={`grid grid-cols-1 md:grid-cols-[1fr_380px] gap-8 w-full pb-14 md:pb-0 transition-opacity ${isLoading ? 'opacity-70' : ''}`}>
      <div className="flex flex-col gap-6">
        <div className="bg-surface-container-high micro-gradient ghost-border rounded-lg p-6">
          <h2 className="font-headline font-bold text-lg flex items-center gap-2 mb-6">
            <span className="material-symbols-outlined text-primary" style={{ fontVariationSettings: "'FILL' 1" }}>public</span>
            Front Door Profile
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col gap-1">
              <div className="flex items-center">
                <span className="text-[11px] font-mono text-outline uppercase tracking-wider">Tier</span>
                <FieldTooltip content="Standard: modern CDN baseline with built-in caching and routing. Premium adds Private Link origins, advanced WAF, and bot protection — note: Premium cannot be downgraded to Standard in-place." />
              </div>
              <Select
                label=""
                value={inputs.tier}
                onChange={(value) => set('tier', value as FrontDoorRequest['tier'])}
                options={[...TIER_OPTIONS]}
              />
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-end">
                <label className="text-[11px] font-mono text-outline uppercase tracking-wider">Requests / month</label>
                <span className="text-xl font-bold font-mono text-primary">{inputs.requestsPerMonth.toLocaleString()}</span>
              </div>
              <Slider
                value={inputs.requestsPerMonth}
                onChange={(value) => set('requestsPerMonth', value)}
                min={0}
                max={100_000_000}
                step={100_000}
                label="Requests / month"
                formatValue={(n) => n.toLocaleString()}
              />
            </div>
          </div>

          {inputs.tier === 'premium' && (
            <div className="mt-4 p-4 bg-[#e8b000]/10 border border-[#e8b000]/40 rounded-sm flex items-start gap-3">
              <span className="material-symbols-outlined text-[#e8b000] text-[18px] shrink-0 mt-0.5">warning</span>
              <p className="text-[12px] leading-relaxed" style={{ color: 'var(--text-primary)' }}>
                <strong className="text-[#e8b000]">Migration warning:</strong> Azure Front Door Premium cannot be downgraded to Standard in-place. Moving down requires a new Standard profile and manual migration.
              </p>
            </div>
          )}
        </div>

        <div className="bg-surface-container-high micro-gradient ghost-border rounded-lg p-6">
          <h2 className="font-headline font-bold text-lg flex items-center gap-2 mb-6">
            <span className="material-symbols-outlined text-[#bc5b00]" style={{ fontVariationSettings: "'FILL' 1" }}>swap_horiz</span>
            Data Transfer
          </h2>

          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-end">
                <label className="text-[11px] font-mono text-outline uppercase tracking-wider">Origin to edge transfer</label>
                <span className="text-lg font-bold font-mono text-on-surface">{inputs.dataTransfer.inboundGB.toLocaleString()} GB</span>
              </div>
              <Slider
                value={inputs.dataTransfer.inboundGB}
                onChange={(value) => patchNested('dataTransfer', { inboundGB: value })}
                min={0}
                max={10_000}
                step={10}
                label="Origin to edge transfer"
                formatValue={(n) => `${n.toLocaleString()} GB`}
              />
              <p className="text-[10px] text-outline italic">Used for visibility only in v1. This calculator treats origin-to-edge traffic as a separate informational line item.</p>
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-end">
                <label className="text-[11px] font-mono text-outline uppercase tracking-wider">Edge to user transfer</label>
                <span className="text-lg font-bold font-mono text-on-surface">{inputs.dataTransfer.outboundGB.toLocaleString()} GB</span>
              </div>
              <Slider
                value={inputs.dataTransfer.outboundGB}
                onChange={(value) => patchNested('dataTransfer', { outboundGB: value })}
                min={0}
                max={10_000}
                step={10}
                label="Edge to user transfer"
                formatValue={(n) => `${n.toLocaleString()} GB`}
              />
              <p className="text-[10px] text-outline italic">Modelled using Azure bandwidth egress tiers after the free 100 GB allowance.</p>
            </div>
          </div>
        </div>

        <div className="bg-surface-container-high micro-gradient ghost-border rounded-lg p-6">
          <h2 className="font-headline font-bold text-lg flex items-center gap-2 mb-6">
            <span className="material-symbols-outlined text-[#a80000]" style={{ fontVariationSettings: "'FILL' 1" }}>security</span>
            Security Features
          </h2>

          <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between p-4 bg-surface-container-lowest/50 border border-outline-variant/50 rounded-sm">
              <div>
                <p className="text-sm font-bold">Web Application Firewall</p>
                <p className="text-[10px] text-outline">Base policy fee plus custom rules on Classic/Standard. Premium is treated as bundled in this v1 estimate.</p>
              </div>
              <Toggle
                checked={inputs.waf.enabled}
                onChange={(checked) => patchNested('waf', { enabled: checked })}
                label={inputs.waf.enabled ? 'Enabled' : 'Disabled'}
              />
            </div>

            {inputs.waf.enabled && inputs.tier !== 'premium' && (
              <div className="flex flex-col gap-2">
                <div className="flex justify-between items-end">
                  <label className="text-[11px] font-mono text-outline uppercase tracking-wider flex items-center">Custom WAF rules<FieldTooltip content="Each custom rule applies a condition + action (allow/block/rate-limit). Billed per rule per month. Managed rule sets (OWASP, Bot) are included in the base WAF policy fee." /></label>
                  <span className="text-lg font-bold font-mono text-on-surface">{inputs.waf.customRuleCount}</span>
                </div>
                <Slider
                  value={inputs.waf.customRuleCount}
                  onChange={(value) => patchNested('waf', { customRuleCount: value })}
                  min={0}
                  max={25}
                  step={1}
                  label="Custom WAF rules"
                  formatValue={(n) => `${n}`}
                />
              </div>
            )}

            <div className="flex flex-col gap-1">
              <div className="flex items-center">
                <span className="text-[11px] font-mono text-outline uppercase tracking-wider">DDoS tier</span>
                <FieldTooltip content="Network Protection: flat monthly fee covering all IPs in the subscription — cost-effective at 14+ IPs. No DDoS plan: platform-level basic protection only, no SLA guarantee." />
              </div>
              <Select
                label=""
                value={inputs.ddos.tier}
                onChange={(value) => patchNested('ddos', { tier: value as FrontDoorRequest['ddos']['tier'] })}
                options={[...DDOS_OPTIONS]}
              />
            </div>

          </div>
        </div>
      </div>

      <div className="flex flex-col gap-6">
        <div className="sticky top-[72px] flex flex-col gap-6">
          <div id="front-door-results" className="bg-surface-container-high micro-gradient border-l-2 border-primary rounded-lg overflow-hidden flex flex-col">
            <div className="p-6">
              <h3 className="font-mono text-[11px] text-outline uppercase tracking-widest mb-2">Estimated Monthly Cost (PAYG)</h3>
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-bold font-headline">
                  {isLoading ? '...' : isError ? 'Error' : `£${visibleTotal?.toFixed(2) ?? data?.monthly_estimate.toFixed(2) ?? '0.00'}`}
                </span>
                {!isLoading && !isError && data && <CurrencyTooltip />}
                <span className="text-outline text-sm">/mo</span>
                <button
                  type="button"
                  onClick={() => setFeedbackOpen(true)}
                  title="Report an issue with this estimate"
                  className="ml-1 transition-colors"
                  style={{ color: 'var(--text-tertiary)' }}
                >
                  <span className="material-symbols-outlined text-[18px]">help</span>
                </button>
              </div>
              <p className="text-[10px] text-outline mt-1 italic uppercase tracking-wider">
                UK South · Azure Front Door {inputs.tier === 'standard' ? 'Standard' : 'Premium'}
              </p>
            </div>

            <div className="bg-surface-container-lowest/50 p-6 space-y-4">
              <h4 className="text-xs font-bold uppercase tracking-wider text-on-surface-variant flex items-center gap-2">
                <span className="material-symbols-outlined text-sm">list_alt</span>
                Cost Breakdown
              </h4>
              <ul className="space-y-3">
                {data?.breakdown.map((item, idx) => (
                  <li key={idx} className="flex justify-between items-center font-mono text-xs gap-3">
                    <span className="text-outline">{item.label}</span>
                    <span className={`font-bold ${item.isFree ? 'text-[#107c10]' : ''}`}>
                      {item.isFree ? 'Free' : `£${item.value.toFixed(2)}`}
                    </span>
                  </li>
                ))}
                {!data && !isLoading && (
                  <p className="text-[10px] text-outline text-center py-4">Adjust inputs to see pricing</p>
                )}
              </ul>
            </div>

            {data?.warnings?.length ? (
              <div className="px-6 pb-4 flex flex-col gap-3">
                {data.warnings.map((warning, idx) => (
                  <div key={idx} className="p-3 bg-[#e8b000]/10 border border-[#e8b000]/40 rounded-sm flex items-start gap-2">
                    <span className="material-symbols-outlined text-[#e8b000] text-[16px] shrink-0 mt-0.5">warning</span>
                    <p className="text-[11px] leading-relaxed" style={{ color: 'var(--text-primary)' }}>{warning}</p>
                  </div>
                ))}
              </div>
            ) : null}

            <div className="p-4 bg-surface-container-highest">
              <ShareButton inputs={inputs} result={data ?? null} service="front-door" />
            </div>
          </div>

          {data?.tier_comparison?.length ? (
            <div className="bg-surface-container-high ghost-border rounded-lg p-5">
              <h4 className="text-[11px] font-mono text-outline uppercase tracking-wider mb-4">Tier Comparison</h4>
              <div className="space-y-3">
                {data.tier_comparison.map((row) => (
                  <button
                    key={row.tier}
                    type="button"
                    onClick={() => set('tier', row.tier.toLowerCase() as FrontDoorRequest['tier'])}
                    className={`w-full p-4 rounded border text-left transition-all ${row.isCurrentSelection ? 'bg-primary/10 border-primary/60' : 'bg-surface-container-lowest border-outline-variant/40 hover:border-outline-variant'}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>
                          {row.tier}
                          {row.isCurrentSelection ? ' (Current)' : ''}
                        </p>
                        {row.isCheapest && !row.isCurrentSelection && (
                          <p className="text-[10px] font-bold uppercase tracking-wider text-[#107c10] mt-1">Cheapest with current assumptions</p>
                        )}
                      </div>
                      <span className="text-sm font-mono font-bold text-primary">£{row.total.toFixed(2)}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {data && (
            <FormulaDisclosure
              lines={[
                `Base profile fee: tier ${inputs.tier}`,
                `Requests: ${inputs.requestsPerMonth.toLocaleString()} per month`,
                `Outbound transfer: ${inputs.dataTransfer.outboundGB.toLocaleString()} GB`,
                `WAF enabled: ${inputs.waf.enabled ? 'yes' : 'no'}${inputs.waf.enabled ? ` · custom rules ${inputs.waf.customRuleCount}` : ''}`,
                `DDoS: ${inputs.ddos.tier}`,
              ]}
              fetchedAt={data.prices_last_updated}
            />
          )}

          {data && (
            <DiscountModifier
              discount={discount}
              onDiscountChange={setDiscount}
              paygTotal={data.monthly_estimate}
            />
          )}

          <div className="bg-surface-container-lowest/50 border border-outline-variant p-4 text-[10px] text-outline leading-relaxed">
            <Disclaimer />
            {data?.disclaimer && <p className="mt-1 italic">{data.disclaimer}</p>}
          </div>

          <ResourcesSection service="front-door" />
        </div>
      </div>

      {feedbackOpen && (
        <FeedbackModal
          service="front-door"
          estimateValue={data ? `£${(visibleTotal ?? data.monthly_estimate).toFixed(2)}/mo` : 'N/A'}
          onClose={() => setFeedbackOpen(false)}
        />
      )}

      <MobileCostBar
        amount={visibleTotal ?? data?.monthly_estimate}
        label="Front Door estimate"
        targetId="front-door-results"
        savingsAmount={data && discount > 0 && visibleTotal != null ? data.monthly_estimate - visibleTotal : undefined}
      />
    </div>
  )
}
